import { useCallback, useEffect, useMemo, useState } from 'react'
import { TicketPercent, Plus, Trash2, ToggleLeft, ToggleRight, ChevronDown, Check } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { cn } from '@/lib/utils'
import {
  useCoupons,
  useCreateCoupon,
  useDeleteCoupon,
  useUpdateCoupon,
  type Coupon,
  type CouponDiscountType,
  type CouponFulfillmentRule,
  type CouponMenuRule,
} from '@/lib/queries/useCoupons'
import { useMenuCategories, useMenuItems, usePOSMenuSync } from '@/lib/queries/usePOS'
import type { MenuCategory, MenuItem } from '@/lib/types/pos'

const DISCOUNT_TYPES: Array<{ value: CouponDiscountType; label: string }> = [
  { value: 'percentage', label: 'Percentage' },
  { value: 'fixed_amount', label: 'Fixed amount' },
  { value: 'free_delivery', label: 'Free delivery' },
]

const FULFILLMENT_RULES: Array<{ value: CouponFulfillmentRule; label: string }> = [
  { value: 'all', label: 'All orders' },
  { value: 'pickup', label: 'Collection only' },
  { value: 'delivery', label: 'Delivery only' },
]

const MENU_RULES: Array<{ value: CouponMenuRule; label: string; hint: string }> = [
  { value: 'all', label: 'All menu items', hint: 'Discount any item in the basket.' },
  { value: 'categories', label: 'Selected categories', hint: 'Discount items inside chosen categories.' },
  { value: 'items', label: 'Selected items', hint: 'Discount only the exact chosen items.' },
]

function normalizeCode(value: string) {
  return value.trim().replace(/\s+/g, '').toUpperCase()
}

function toLocalDatetimeValue(value?: string | null) {
  if (!value) return ''
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return ''
  const offsetMs = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16)
}

function fromLocalDatetimeValue(value: string) {
  return value ? new Date(value).toISOString() : null
}

function formatRule(coupon: Coupon) {
  if (coupon.discountType === 'free_delivery') return 'Free delivery'
  const value = Number(coupon.discountValue ?? 0) || 0
  if (coupon.discountType === 'percentage') return `${value}% off`
  return `€${value.toFixed(2)} off`
}

interface CouponFormValues {
  code: string
  name: string
  active: boolean
  discountType: CouponDiscountType
  discountValue: string
  minimumSubtotal: string
  maximumDiscount: string
  usageLimit: string
  appliesToFulfillment: CouponFulfillmentRule
  appliesToMenu: CouponMenuRule
  selectedCategoryIds: string[]
  selectedItemIds: string[]
  startsAt: string
  endsAt: string
}

function getCouponFormValues(coupon?: Coupon | null): CouponFormValues {
  return {
    code: coupon?.code ?? '',
    name: coupon?.name ?? '',
    active: coupon?.active ?? true,
    discountType: coupon?.discountType ?? 'percentage',
    discountValue: String(coupon?.discountValue ?? '10'),
    minimumSubtotal: String(coupon?.minimumSubtotal ?? ''),
    maximumDiscount: String(coupon?.maximumDiscount ?? ''),
    usageLimit: coupon?.usageLimit == null ? '' : String(coupon.usageLimit),
    appliesToFulfillment: coupon?.appliesToFulfillment ?? 'all',
    appliesToMenu: coupon?.appliesToMenu ?? 'all',
    selectedCategoryIds: (coupon?.menuCategories ?? []).map((entry) => entry.documentId),
    selectedItemIds: (coupon?.menuItems ?? []).map((entry) => entry.documentId),
    startsAt: toLocalDatetimeValue(coupon?.startsAt),
    endsAt: toLocalDatetimeValue(coupon?.endsAt),
  }
}

function sameIds(left: string[], right: string[]) {
  if (left.length !== right.length) return false
  const sortedLeft = [...left].sort()
  const sortedRight = [...right].sort()
  return sortedLeft.every((value, index) => value === sortedRight[index])
}

function sameCouponFormValues(left: CouponFormValues, right: CouponFormValues) {
  return (
    left.code === right.code &&
    left.name === right.name &&
    left.active === right.active &&
    left.discountType === right.discountType &&
    left.discountValue === right.discountValue &&
    left.minimumSubtotal === right.minimumSubtotal &&
    left.maximumDiscount === right.maximumDiscount &&
    left.usageLimit === right.usageLimit &&
    left.appliesToFulfillment === right.appliesToFulfillment &&
    left.appliesToMenu === right.appliesToMenu &&
    sameIds(left.selectedCategoryIds, right.selectedCategoryIds) &&
    sameIds(left.selectedItemIds, right.selectedItemIds) &&
    left.startsAt === right.startsAt &&
    left.endsAt === right.endsAt
  )
}

function CouponForm({
  coupon,
  onCancel,
  onSaved,
  onDirtyChange,
  categories,
  menuItems,
}: {
  coupon?: Coupon | null
  onCancel?: () => void
  onSaved?: () => void
  onDirtyChange?: (dirty: boolean) => void
  categories: MenuCategory[]
  menuItems: MenuItem[]
}) {
  const createCoupon = useCreateCoupon()
  const updateCoupon = useUpdateCoupon()
  const initialValues = useMemo(() => getCouponFormValues(coupon), [coupon])
  const [code, setCode] = useState(initialValues.code)
  const [name, setName] = useState(initialValues.name)
  const [active, setActive] = useState(initialValues.active)
  const [discountType, setDiscountType] = useState<CouponDiscountType>(initialValues.discountType)
  const [discountValue, setDiscountValue] = useState(initialValues.discountValue)
  const [minimumSubtotal, setMinimumSubtotal] = useState(initialValues.minimumSubtotal)
  const [maximumDiscount, setMaximumDiscount] = useState(initialValues.maximumDiscount)
  const [usageLimit, setUsageLimit] = useState(initialValues.usageLimit)
  const [appliesToFulfillment, setAppliesToFulfillment] = useState<CouponFulfillmentRule>(initialValues.appliesToFulfillment)
  const [appliesToMenu, setAppliesToMenu] = useState<CouponMenuRule>(initialValues.appliesToMenu)
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(initialValues.selectedCategoryIds)
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>(initialValues.selectedItemIds)
  const [expandedItemCategoryIds, setExpandedItemCategoryIds] = useState<string[]>([])
  const [startsAt, setStartsAt] = useState(initialValues.startsAt)
  const [endsAt, setEndsAt] = useState(initialValues.endsAt)

  const itemGroups = useMemo(() => {
    const categoryOrder = new Map(categories.map((category, index) => [category.documentId, index]))
    const groups = new Map<string, { id: string; name: string; items: MenuItem[]; sortOrder: number }>()

    for (const item of menuItems) {
      const categoryId = item.category?.documentId ?? '__uncategorised'
      const categoryName = item.category?.name ?? 'Uncategorised'
      const group = groups.get(categoryId) ?? {
        id: categoryId,
        name: categoryName,
        items: [],
        sortOrder: categoryId === '__uncategorised' ? Number.MAX_SAFE_INTEGER : categoryOrder.get(categoryId) ?? Number.MAX_SAFE_INTEGER - 1,
      }
      group.items.push(item)
      groups.set(categoryId, group)
    }

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        items: group.items.slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
  }, [categories, menuItems])

  const defaultExpandedItemCategoryIds = useMemo(() => {
    if (appliesToMenu !== 'items' || itemGroups.length === 0 || expandedItemCategoryIds.length > 0) return
    const selectedGroups = itemGroups
      .filter((group) => group.items.some((item) => selectedItemIds.includes(item.documentId)))
      .map((group) => group.id)
    return selectedGroups.length > 0 ? selectedGroups : [itemGroups[0].id]
  }, [appliesToMenu, expandedItemCategoryIds.length, itemGroups, selectedItemIds])
  const effectiveExpandedItemCategoryIds = expandedItemCategoryIds.length > 0
    ? expandedItemCategoryIds
    : defaultExpandedItemCategoryIds ?? []

  const loading = createCoupon.isPending || updateCoupon.isPending
  const isFreeDelivery = discountType === 'free_delivery'
  const currentValues = useMemo<CouponFormValues>(
    () => ({
      code,
      name,
      active,
      discountType,
      discountValue,
      minimumSubtotal,
      maximumDiscount,
      usageLimit,
      appliesToFulfillment,
      appliesToMenu,
      selectedCategoryIds,
      selectedItemIds,
      startsAt,
      endsAt,
    }),
    [
      active,
      appliesToFulfillment,
      appliesToMenu,
      code,
      discountType,
      discountValue,
      endsAt,
      maximumDiscount,
      minimumSubtotal,
      name,
      selectedCategoryIds,
      selectedItemIds,
      startsAt,
      usageLimit,
    ],
  )
  const dirty = useMemo(() => !sameCouponFormValues(currentValues, initialValues), [currentValues, initialValues])

  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    const payload = {
      code: normalizeCode(code),
      name: name.trim(),
      active,
      discountType,
      discountValue: isFreeDelivery ? 0 : Math.max(0, Number(discountValue) || 0),
      minimumSubtotal: Math.max(0, Number(minimumSubtotal) || 0),
      maximumDiscount: maximumDiscount.trim() ? Math.max(0, Number(maximumDiscount) || 0) : null,
      usageLimit: usageLimit.trim() ? Math.max(0, Math.floor(Number(usageLimit) || 0)) : null,
      appliesToFulfillment,
      appliesToMenu,
      menuCategoryDocumentIds: appliesToMenu === 'categories' ? selectedCategoryIds : [],
      menuItemDocumentIds: appliesToMenu === 'items' ? selectedItemIds : [],
      startsAt: fromLocalDatetimeValue(startsAt),
      endsAt: fromLocalDatetimeValue(endsAt),
    }
    if (!payload.code) return
    if (coupon?.documentId) await updateCoupon.mutateAsync({ documentId: coupon.documentId, ...payload })
    else await createCoupon.mutateAsync(payload)
    if (!coupon) {
      setCode('')
      setName('')
      setDiscountType('percentage')
      setDiscountValue('10')
      setMinimumSubtotal('')
      setMaximumDiscount('')
      setUsageLimit('')
      setAppliesToFulfillment('all')
      setAppliesToMenu('all')
      setSelectedCategoryIds([])
      setSelectedItemIds([])
      setStartsAt('')
      setEndsAt('')
      setActive(true)
    }
    onSaved?.()
  }

  const toggleId = (id: string, values: string[], setter: (next: string[]) => void) => {
    setter(values.includes(id) ? values.filter((entry) => entry !== id) : [...values, id])
  }

  const toggleItemCategory = (id: string) => {
    setExpandedItemCategoryIds((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    )
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        <Input label="Coupon code" value={code} onChange={(e) => setCode(normalizeCode(e.target.value))} placeholder="WELCOME10" />
        <Input label="Display name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Welcome discount" />
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <label className="space-y-1 text-sm font-medium text-[var(--color-text-secondary)]">
          <span>Discount type</span>
          <select value={discountType} onChange={(e) => setDiscountType(e.target.value as CouponDiscountType)} className="h-10 w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 text-sm">
            {DISCOUNT_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
          </select>
        </label>
        <Input label={discountType === 'percentage' ? 'Discount %' : 'Discount amount'} value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} disabled={isFreeDelivery} type="number" min="0" step="0.01" />
        <label className="space-y-1 text-sm font-medium text-[var(--color-text-secondary)]">
          <span>Applies to</span>
          <select value={appliesToFulfillment} onChange={(e) => setAppliesToFulfillment(e.target.value as CouponFulfillmentRule)} className="h-10 w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 text-sm">
            {FULFILLMENT_RULES.map((rule) => <option key={rule.value} value={rule.value}>{rule.label}</option>)}
          </select>
        </label>
      </div>

      <div className="space-y-3 rounded-[var(--radius-lg)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3">
        <label className="space-y-1 text-sm font-medium text-[var(--color-text-secondary)]">
          <span>Applies to menu</span>
          <select value={appliesToMenu} onChange={(e) => setAppliesToMenu(e.target.value as CouponMenuRule)} className="h-10 w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 text-sm">
            {MENU_RULES.map((rule) => <option key={rule.value} value={rule.value}>{rule.label}</option>)}
          </select>
        </label>
        <p className="text-xs text-[var(--color-text-muted)]">
          {MENU_RULES.find((rule) => rule.value === appliesToMenu)?.hint}
        </p>

        {appliesToMenu === 'categories' && (
          <div className="grid max-h-56 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
            {categories.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)]">No categories found.</p>
            ) : categories.map((category) => {
              const checked = selectedCategoryIds.includes(category.documentId)
              return (
                <button
                  key={category.documentId}
                  type="button"
                  onClick={() => toggleId(category.documentId, selectedCategoryIds, setSelectedCategoryIds)}
                  className={cn('rounded-[var(--radius-md)] border px-3 py-2 text-left text-sm transition-colors', checked ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]' : 'border-[var(--color-border-default)] bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)]')}
                >
                  {category.name}
                </button>
              )
            })}
          </div>
        )}

        {appliesToMenu === 'items' && (
          <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
            {menuItems.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)]">No menu items found.</p>
            ) : (
              itemGroups.map((group) => {
                const selectedCount = group.items.filter((item) => selectedItemIds.includes(item.documentId)).length
                const expanded = effectiveExpandedItemCategoryIds.includes(group.id)
                return (
                  <section key={group.id} className="overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)]">
                    <button
                      type="button"
                      onClick={() => toggleItemCategory(group.id)}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-[var(--color-text-primary)]">{group.name}</span>
                        <span className="block text-xs text-[var(--color-text-muted)]">
                          {selectedCount} selected of {group.items.length}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        {selectedCount > 0 && (
                          <span className="rounded-full bg-[var(--color-accent-amber-dim)] px-2 py-0.5 text-xs font-bold text-[var(--color-accent-amber)]">
                            {selectedCount}
                          </span>
                        )}
                        <ChevronDown className={cn('h-4 w-4 text-[var(--color-text-muted)] transition-transform', expanded && 'rotate-180')} />
                      </span>
                    </button>
                    {expanded && (
                      <div className="grid gap-1 border-t border-[var(--color-border-subtle)] p-2">
                        {group.items.map((item) => {
                          const checked = selectedItemIds.includes(item.documentId)
                          return (
                            <button
                              key={item.documentId}
                              type="button"
                              onClick={() => toggleId(item.documentId, selectedItemIds, setSelectedItemIds)}
                              className={cn('flex items-center justify-between gap-3 rounded-[var(--radius-sm)] px-3 py-2 text-left text-sm transition-colors', checked ? 'bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]' : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]')}
                            >
                              <span className="min-w-0 truncate font-medium">{item.name}</span>
                              <span className={cn('grid h-5 w-5 shrink-0 place-items-center rounded-full border text-[10px]', checked ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber)] text-white' : 'border-[var(--color-border-default)] text-transparent')}>
                                <Check className="h-3 w-3" />
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </section>
                )
              })
            )}
          </div>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <Input label="Minimum subtotal" value={minimumSubtotal} onChange={(e) => setMinimumSubtotal(e.target.value)} type="number" min="0" step="0.01" placeholder="0.00" />
        <Input label="Max discount" value={maximumDiscount} onChange={(e) => setMaximumDiscount(e.target.value)} type="number" min="0" step="0.01" placeholder="Optional" disabled={discountType !== 'percentage'} />
        <Input label="Usage limit" value={usageLimit} onChange={(e) => setUsageLimit(e.target.value)} type="number" min="0" step="1" placeholder="Unlimited" />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Input label="Starts at" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} type="datetime-local" />
        <Input label="Ends at" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} type="datetime-local" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={() => setActive((v) => !v)} className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-text-secondary)]">
          {active ? <ToggleRight className="h-5 w-5 text-[var(--color-accent-amber)]" /> : <ToggleLeft className="h-5 w-5" />}
          {active ? 'Active' : 'Inactive'}
        </button>
        <div className="flex gap-2">
          {onCancel && <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>}
          <Button type="submit" loading={loading} disabled={!normalizeCode(code)}>
            {coupon ? 'Save coupon' : 'Create coupon'}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default function CouponsPage() {
  usePOSMenuSync()
  const { data: couponsResp, isLoading } = useCoupons()
  const coupons: Coupon[] = useMemo(() => couponsResp?.data ?? [], [couponsResp])
  const { data: categoriesResp } = useMenuCategories()
  const categories: MenuCategory[] = categoriesResp?.data ?? []
  const { data: menuItemsResp } = useMenuItems()
  const menuItems: MenuItem[] = menuItemsResp?.data ?? []
  const deleteCoupon = useDeleteCoupon()
  const [editing, setEditing] = useState<Coupon | null>(null)
  const [formDirty, setFormDirty] = useState(false)
  const [formResetKey, setFormResetKey] = useState(0)

  const activeCount = useMemo(() => coupons.filter((coupon) => coupon.active !== false).length, [coupons])
  const selectedCouponId = editing?.documentId ?? null

  const discardChanges = useCallback(
    (message: string) => !formDirty || window.confirm(message),
    [formDirty],
  )

  const startNewCoupon = useCallback(() => {
    if (!discardChanges('Discard unsaved coupon changes and start a new coupon?')) return
    setEditing(null)
    setFormDirty(false)
    setFormResetKey((key) => key + 1)
  }, [discardChanges])

  const finishCouponSave = useCallback(() => {
    setEditing(null)
    setFormDirty(false)
    setFormResetKey((key) => key + 1)
  }, [])

  const editCoupon = useCallback(
    (coupon: Coupon) => {
      if (coupon.documentId === selectedCouponId) return
      if (!discardChanges(`Discard unsaved coupon changes and edit "${coupon.code}"?`)) return
      setEditing(coupon)
      setFormDirty(false)
      setFormResetKey((key) => key + 1)
    },
    [discardChanges, selectedCouponId],
  )

  return (
    <div>
      <PageHeader title="Coupons" description="Create discount codes for the public ordering checkout." />
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
            <CardTitle>Active coupons</CardTitle>
            <Button type="button" variant="secondary" size="sm" onClick={startNewCoupon}>
              <Plus className="h-4 w-4" />
              New coupon
            </Button>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="py-10 text-center text-sm text-[var(--color-text-muted)]">Loading coupons...</div>
            ) : coupons.length === 0 ? (
              <div className="rounded-[var(--radius-lg)] border border-dashed border-[var(--color-border-default)] px-6 py-10 text-center">
                <TicketPercent className="mx-auto mb-3 h-8 w-8 text-[var(--color-text-muted)]" />
                <p className="text-sm font-medium text-[var(--color-text-primary)]">No coupons yet</p>
                <p className="mt-1 text-sm text-[var(--color-text-muted)]">Create one and visitors can apply it during checkout.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-sm text-[var(--color-text-muted)]">{activeCount} active of {coupons.length}</div>
                {coupons.map((coupon) => {
                  const selected = coupon.documentId === selectedCouponId
                  return (
                    <div key={coupon.documentId} className={cn('rounded-[var(--radius-lg)] border p-4 transition-colors', selected ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber-dim)] ring-1 ring-[var(--color-accent-amber)]/40' : coupon.active === false ? 'border-[var(--color-border-subtle)] opacity-60' : 'border-[var(--color-border-default)]')}>
                      <div className="flex items-start justify-between gap-3">
                        <button type="button" onClick={() => editCoupon(coupon)} className="min-w-0 text-left">
                          <span className="inline-flex rounded-full bg-[var(--color-accent-amber-dim)] px-2.5 py-1 font-mono text-xs font-bold text-[var(--color-accent-amber)]">{coupon.code}</span>
                          <p className="mt-2 text-sm font-semibold text-[var(--color-text-primary)]">{coupon.name || coupon.code}</p>
                          <p className="text-xs text-[var(--color-text-muted)]">
                            {formatRule(coupon)} - {FULFILLMENT_RULES.find((rule) => rule.value === (coupon.appliesToFulfillment ?? 'all'))?.label}
                          </p>
                          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                            {coupon.appliesToMenu === 'categories'
                              ? `${coupon.menuCategories?.length ?? 0} categor${(coupon.menuCategories?.length ?? 0) === 1 ? 'y' : 'ies'}`
                              : coupon.appliesToMenu === 'items'
                                ? `${coupon.menuItems?.length ?? 0} item${(coupon.menuItems?.length ?? 0) === 1 ? '' : 's'}`
                                : 'All menu items'}
                          </p>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm(`Delete coupon "${coupon.code}"?`)) deleteCoupon.mutate(coupon.documentId)
                          }}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-muted)] transition-colors hover:bg-red-500/10 hover:text-[var(--color-accent-red)]"
                          aria-label={`Delete ${coupon.code}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              {editing ? `Edit ${editing.code}` : 'New coupon'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CouponForm
              key={`${editing?.documentId ?? 'new'}-${formResetKey}`}
              coupon={editing}
              onDirtyChange={setFormDirty}
              onSaved={finishCouponSave}
              onCancel={editing ? startNewCoupon : undefined}
              categories={categories}
              menuItems={menuItems}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
